/**
 * AI Scribe V2 — clinical event card
 *
 * Colour-coded by category: a 3px accent rail, an uppercase category label with
 * its icon, then the one-line clinical summary.
 *
 * @author [Rajkumar Rathod]
 */

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import SentimentDissatisfiedOutlinedIcon from "@mui/icons-material/SentimentDissatisfiedOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { ReactNode } from "react";
import Flex from "../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../components/Text";
import {
  PRIMITIVE,
  px,
} from "../../../../../../../../../../tokens/design-tokens";
import { CLINICAL_EVENT_META, SCRIBE_V2_COLORS } from "../constants";
import { ClinicalEvent, ClinicalEventCategory } from "../types";

const CATEGORY_ICONS: Record<ClinicalEventCategory, ReactNode> = {
  SYMPTOM: <MonitorHeartOutlinedIcon />,
  STRESSOR: <SentimentDissatisfiedOutlinedIcon />,
  RISK_SCREEN: <ShieldOutlinedIcon />,
  MEDICATION: <MedicationOutlinedIcon />,
  THERAPY: <PsychologyOutlinedIcon />,
  PLAN: <AssignmentOutlinedIcon />,
  FOLLOW_UP: <EventOutlinedIcon />,
  DIAGNOSIS: <LocalHospitalOutlinedIcon />,
  ALLERGY: <WarningAmberOutlinedIcon />,
  VITALS: <FavoriteBorderOutlinedIcon />,
  OTHER: <NoteAltOutlinedIcon />,
};

interface ClinicalEventCardProps {
  event: ClinicalEvent;
}

const ClinicalEventCard = ({ event }: ClinicalEventCardProps) => {
  const meta = CLINICAL_EVENT_META[event.category] ?? CLINICAL_EVENT_META.OTHER;

  return (
    <Flex
      flexDirection="column"
      gap={0.5}
      px={px(PRIMITIVE.spacing[8])}
      py={px(PRIMITIVE.spacing[8])}
      sx={{
        backgroundColor: meta.bg,
        borderLeft: `4px solid ${meta.accent}`,
        borderRadius: `0 ${px(PRIMITIVE.radius[4])} ${px(
          PRIMITIVE.radius[4]
        )} 0`,
        flexShrink: 0,
      }}
    >
      <Flex alignItems="center" gap={0.5} sx={{ color: meta.accent }}>
        <Flex
          alignItems="center"
          sx={{ "& svg": { fontSize: px(PRIMITIVE.spacing[12]) } }}
        >
          {CATEGORY_ICONS[event.category] ?? CATEGORY_ICONS.OTHER}
        </Flex>
        <Text
          sx={{
            color: meta.accent,
            fontSize: px(PRIMITIVE.typography.fontSize.body.b6),
            fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {meta.label}
        </Text>
      </Flex>

      <Text
        sx={{
          color: SCRIBE_V2_COLORS.textPrimary,
          fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
          fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
          lineHeight: px(PRIMITIVE.typography.lineHeight["150%"][12]),
        }}
      >
        {event.summary}
      </Text>

      {!!event.detail && (
        <Text
          sx={{
            color: SCRIBE_V2_COLORS.textSecondary,
            fontSize: px(PRIMITIVE.typography.fontSize.body.b6),
            lineHeight: px(PRIMITIVE.typography.lineHeight["150%"][10]),
          }}
        >
          {event.detail}
        </Text>
      )}
    </Flex>
  );
};

export default ClinicalEventCard;
